import React, { useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box } from "@mui/material";
import clusterProductImage from "../../assets/products/tamales/GREEN_SALSA_TAMALE/GREEN_SALSA_TAMALE.png";

const SOURCE_ID = "stores";
const BRAND = "#803D1F";

const CLUSTER_IMAGE_ID = "cluster-product-icon";
const STORE_IMAGE_ID = "store-product-icon";
const USER_SOURCE_ID = "user-location";
const USER_PULSE_LAYER_ID = "user-location-pulse";
const USER_DOT_LAYER_ID = "user-location-dot";

function toStoresGeoJSON(stores = []) {
    return {
        type: "FeatureCollection",
        features: stores
            .filter((s) => Array.isArray(s.coordinates) && s.coordinates.length === 2)
            .map((s) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: s.coordinates, // [lng, lat]
                },
                properties: {
                    id: s.id,
                    name: s.name,
                    address: s.address,
                    state: s.state,
                    addressUrl: s.addressUrl || "",
                },
            })),
    };
}

function getGeoJSONBounds(featureCollection) {
    const coords =
        featureCollection?.features
            ?.map((f) => f.geometry?.coordinates)
            .filter((c) => Array.isArray(c) && c.length === 2) || [];

    if (!coords.length) return null;

    let minLng = coords[0][0];
    let minLat = coords[0][1];
    let maxLng = coords[0][0];
    let maxLat = coords[0][1];

    for (const [lng, lat] of coords) {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
    }

    return [
        [minLng, minLat],
        [maxLng, maxLat],
    ];
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function buildPopupHTML({ name, address, addressUrl }) {
    const safeName = escapeHtml(name);
    const safeAddress = escapeHtml(address);
    const safeUrl = typeof addressUrl === "string" ? addressUrl : "";
    const hasDirections = Boolean(safeUrl);

    const addressRow = `
    <div style="
      font-family:'Cervino Neue', Arial, sans-serif;
      font-weight:700;
      font-size:14px;
      line-height:1.35;
      color:#111;
      opacity:0.9;
      margin-bottom:${hasDirections ? "14px" : "0"};
    ">
      ${safeAddress}
    </div>
  `;

    const directionsButton = hasDirections
        ? `
          <div style="display:flex; justify-content:center; margin-top:4px;">
            <a
              href="${safeUrl}"
              target="_blank"
              rel="noopener noreferrer"
              style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                gap:8px;
                text-decoration:none;
                padding:8px 14px;
                border-radius:999px;
                border:2px solid ${BRAND};
                color:${BRAND};
                font-weight:900;
                font-family:'Cervino Neue', Arial, sans-serif;
                font-size:16px;
                letter-spacing:0.3px;
                line-height:1;
                transition: background-color 160ms ease, color 160ms ease;
                user-select:none;
              "
              onmouseenter="
                this.style.backgroundColor='${BRAND}';
                this.style.color='#fff';
                const svg=this.querySelector('svg'); if(svg) svg.style.fill='#fff';
              "
              onmouseleave="
                this.style.backgroundColor='transparent';
                this.style.color='${BRAND}';
                const svg=this.querySelector('svg'); if(svg) svg.style.fill='${BRAND}';
              "
              aria-label="Get directions"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                style="display:block; fill:${BRAND};"
              >
                <path d="M12 2c-3.86 0-7 3.14-7 7 0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
              </svg>
              DIRECTIONS
            </a>
          </div>
        `
        : "";

    return `
    <div style="font-family:'Cervino Neue', Arial, sans-serif; font-size:14px;">
      <div style="
        font-weight:900;
        font-size:18px;
        margin-bottom:8px;
        color:${BRAND};
        padding-right: 5px;
        padding-top: 15px;
      ">
        ${safeName}
      </div>

      ${addressRow}
      ${directionsButton}
    </div>
  `;
}

// ✅ NEW: helpers for user point + miles
function toPointGeoJSON(coords) {
    if (!Array.isArray(coords) || coords.length !== 2) return null;
    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                geometry: { type: "Point", coordinates: coords },
                properties: {},
            },
        ],
    };
}

export default function MapBoxMap({
                                      accessToken,
                                      stores = [],
                                      fitStores = null,
                                      initialCenter = [-98.5795, 39.8283],
                                      initialZoom = 3,
                                      styleUrl = "mapbox://styles/mapbox/streets-v12",
                                      fitToStoresOnLoad = true,
                                      fitToStoresOnUpdate = false,
                                      fitBoundsPadding = 60,
                                      fitBoundsMaxZoom = 12,
                                      selectedStoreId = null,
                                      selectedStoreZoom = 13,
                                      userCoords = null,
                                      onMapReady,
                                      sx,
                                  }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const popupRef = useRef(null);

    const onMapReadyRef = useRef(onMapReady);
    const didFireReadyRef = useRef(false);

    const tryFireReady = (map) => {
        if (!map || didFireReadyRef.current) return;
        didFireReadyRef.current = true;

        // Wait for a stable rendered frame after all layers/images are applied
        map.once("idle", () => {
            onMapReadyRef.current?.();
        });
    };

    useEffect(() => {
        onMapReadyRef.current = onMapReady;
    }, [onMapReady]);

    // ✅ NEW: track map loaded + pending selection
    const isMapLoadedRef = useRef(false);
    const pendingSelectedIdRef = useRef(null);

    const storesGeoJSON = useMemo(() => toStoresGeoJSON(stores), [stores]);

    const fitGeoJSON = useMemo(
        () => toStoresGeoJSON(fitStores?.length ? fitStores : stores),
        [fitStores, stores]
    );

    // ✅ NEW: quick lookup by id so we can flyTo on list clicks
    const storeById = useMemo(() => {
        const m = new Map();
        for (const s of stores || []) m.set(String(s.id), s);
        return m;
    }, [stores]);

    // ✅ NEW: centralized “open popup” helper (used by clicks + sidebar selection)
    const openStorePopupRef = useRef(null);
    openStorePopupRef.current = (map, store) => {
        if (!map || !store?.coordinates) return;

        const [lng, lat] = store.coordinates;
        popupRef.current?.remove();
        popupRef.current = new mapboxgl.Popup({ offset: 12, focusAfterOpen: false })
            .setLngLat([lng, lat])
            .setHTML(
                buildPopupHTML({
                    name: store.name,
                    address: store.address,
                    addressUrl: store.addressUrl,
                })
            )
            .addTo(map);
    };

    // Keep latest values available to the "load" handler without re-creating the map
    const latestRef = useRef({
        initialCenter,
        initialZoom,
        storesGeoJSON,
        fitGeoJSON,
        fitToStoresOnLoad,
        fitBoundsPadding,
        fitBoundsMaxZoom,
        userCoords,
        storeById,
        selectedStoreZoom,
    });

    useEffect(() => {
        latestRef.current = {
            initialCenter,
            initialZoom,
            storesGeoJSON,
            fitGeoJSON,
            fitToStoresOnLoad,
            fitBoundsPadding,
            fitBoundsMaxZoom,
            userCoords,
            storeById,
            selectedStoreZoom,
        };
    }, [
        initialCenter,
        initialZoom,
        storesGeoJSON,
        fitGeoJSON,
        fitToStoresOnLoad,
        fitBoundsPadding,
        fitBoundsMaxZoom,
        userCoords,
        storeById,
        selectedStoreZoom,
    ]);

    useEffect(() => {
        if (!containerRef.current) return;
        if (mapRef.current) return;

        if (!accessToken) {
            console.error("MapBoxMap missing accessToken prop");
            return;
        }

        mapboxgl.accessToken = accessToken;

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: styleUrl,
            center: latestRef.current.initialCenter,
            zoom: latestRef.current.initialZoom,
        });

        mapRef.current = map;
        map.addControl(new mapboxgl.NavigationControl(), "top-right");

        map.on("load", () => {
            isMapLoadedRef.current = true;

            // ===== MAP BRAND OVERRIDES =====
            const layers = map.getStyle().layers;
            layers.forEach((layer) => {
                if (layer.id.includes("water")) {
                    if (layer.type === "fill") {
                        map.setPaintProperty(layer.id, "fill-color", "#EED8C4");
                        map.setPaintProperty(layer.id, "fill-opacity", 1);
                    }
                    if (layer.type === "line") {
                        map.setPaintProperty(layer.id, "line-opacity", 0);
                    }
                }

                if (layer.id.includes("land")) {
                    if (layer.type === "fill") {
                        map.setPaintProperty(layer.id, "fill-color", "#F9F0E0");
                    }
                }

                if (
                    layer.id.includes("boundary") ||
                    layer.id.includes("admin") ||
                    layer.id.includes("border")
                ) {
                    if (layer.type === "line") {
                        map.setPaintProperty(layer.id, "line-color", "#803D1F");
                        map.setPaintProperty(layer.id, "line-width", 1);
                    }
                }
            });

            if (!map.getSource(SOURCE_ID)) {
                map.addSource(SOURCE_ID, {
                    type: "geojson",
                    data: latestRef.current.storesGeoJSON,
                    cluster: true,
                    clusterMaxZoom: 14,
                    clusterRadius: 100,
                });
            }

            // ✅ NEW: USER LOCATION (source + layers)
            if (!map.getSource(USER_SOURCE_ID)) {
                map.addSource(USER_SOURCE_ID, {
                    type: "geojson",
                    data:
                        toPointGeoJSON(latestRef.current.userCoords) || {
                            type: "FeatureCollection",
                            features: [],
                        },
                });
            }

            if (!map.getLayer(USER_PULSE_LAYER_ID)) {
                map.addLayer({
                    id: USER_PULSE_LAYER_ID,
                    type: "circle",
                    source: USER_SOURCE_ID,
                    paint: {
                        "circle-radius": 18,
                        "circle-color": BRAND,
                        "circle-opacity": 0.18,
                    },
                });
            }

            if (!map.getLayer(USER_DOT_LAYER_ID)) {
                map.addLayer({
                    id: USER_DOT_LAYER_ID,
                    type: "circle",
                    source: USER_SOURCE_ID,
                    paint: {
                        "circle-radius": 7,
                        "circle-color": BRAND,
                        "circle-stroke-width": 3,
                        "circle-stroke-color": "#ffffff",
                    },
                });
            }

            if (latestRef.current.fitToStoresOnLoad) {
                const bounds = getGeoJSONBounds(latestRef.current.fitGeoJSON);
                if (bounds) {
                    map.fitBounds(bounds, {
                        padding: latestRef.current.fitBoundsPadding,
                        maxZoom: latestRef.current.fitBoundsMaxZoom,
                        duration: 0,
                    });
                }
            }

            // ======= CLUSTERS =======
            map.addLayer({
                id: "clusters-bg",
                type: "circle",
                source: SOURCE_ID,
                filter: ["has", "point_count"],
                paint: {
                    "circle-radius": ["step", ["get", "point_count"], 40, 10, 45, 30, 50, 60, 55],
                    "circle-color": "#ffffff",
                    "circle-opacity": 0.9,
                    "circle-stroke-width": 3,
                    "circle-stroke-color": BRAND,
                },
            });

            map.loadImage(clusterProductImage, (err, image) => {
                if (err || !image) {
                    console.error("Failed to load cluster image:", err);
                    tryFireReady(map);
                    return;
                }

                if (!map.hasImage(CLUSTER_IMAGE_ID)) map.addImage(CLUSTER_IMAGE_ID, image, { pixelRatio: 2 });
                if (!map.hasImage(STORE_IMAGE_ID)) map.addImage(STORE_IMAGE_ID, image, { pixelRatio: 2 });

                map.addLayer({
                    id: "clusters-icon",
                    type: "symbol",
                    source: SOURCE_ID,
                    filter: ["has", "point_count"],
                    layout: {
                        "icon-image": CLUSTER_IMAGE_ID,
                        "icon-size": ["step", ["get", "point_count"], 0.15, 10, 0.17, 30, 0.19, 60, 0.21],
                        "icon-allow-overlap": true,
                        "icon-ignore-placement": true,
                    },
                });

                map.addLayer({
                    id: "store-badge-bg",
                    type: "circle",
                    source: SOURCE_ID,
                    filter: ["!", ["has", "point_count"]],
                    paint: {
                        "circle-radius": 28,
                        "circle-color": "#ffffff",
                        "circle-opacity": 0.95,
                        "circle-stroke-width": 3,
                        "circle-stroke-color": BRAND,
                    },
                });

                map.addLayer({
                    id: "store-badge-icon",
                    type: "symbol",
                    source: SOURCE_ID,
                    filter: ["!", ["has", "point_count"]],
                    layout: {
                        "icon-image": STORE_IMAGE_ID,
                        "icon-size": 0.11,
                        "icon-allow-overlap": true,
                        "icon-ignore-placement": true,
                    },
                });
                tryFireReady(map);
            });

            // Cursor affordance
            map.on("mouseenter", "clusters-bg", () => (map.getCanvas().style.cursor = "pointer"));
            map.on("mouseleave", "clusters-bg", () => (map.getCanvas().style.cursor = ""));

            map.on("mouseenter", "store-badge-bg", () => (map.getCanvas().style.cursor = "pointer"));
            map.on("mouseleave", "store-badge-bg", () => (map.getCanvas().style.cursor = ""));
            map.on("mouseenter", "store-badge-icon", () => (map.getCanvas().style.cursor = "pointer"));
            map.on("mouseleave", "store-badge-icon", () => (map.getCanvas().style.cursor = ""));

            // Click cluster to zoom in
            map.on("click", "clusters-bg", (e) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ["clusters-bg"] });
                const clusterId = features?.[0]?.properties?.cluster_id;
                if (clusterId == null) return;

                const source = map.getSource(SOURCE_ID);
                source.getClusterExpansionZoom(clusterId, (zoomErr, zoom) => {
                    if (zoomErr) return;
                    map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom,
                    });
                });
            });

            // Click single store badge -> popup
            const onStoreClick = (e) => {
                const feature = e.features?.[0];
                if (!feature) return;

                const { id, name, address, addressUrl } = feature.properties || {};
                const coordinates = feature.geometry.coordinates.slice();

                popupRef.current?.remove();
                popupRef.current = new mapboxgl.Popup({ offset: 12, focusAfterOpen: false })
                    .setLngLat(coordinates)
                    .setHTML(buildPopupHTML({ name, address, addressUrl }))
                    .addTo(map);

                // keep selection consistent if parent is using it
                pendingSelectedIdRef.current = String(id);
            };

            map.on("click", "store-badge-bg", onStoreClick);
            map.on("click", "store-badge-icon", onStoreClick);

            // ✅ if sidebar selected something before the map finished loading, apply it now
            if (pendingSelectedIdRef.current) {
                const pendingStore = latestRef.current.storeById.get(String(pendingSelectedIdRef.current));
                if (pendingStore) {
                    map.easeTo({
                        center: pendingStore.coordinates,
                        zoom: latestRef.current.selectedStoreZoom,
                        duration: 700,
                    });
                    openStorePopupRef.current?.(map, pendingStore);
                }
                pendingSelectedIdRef.current = null;
            }
        });

        return () => {
            popupRef.current?.remove();
            map.remove();
            mapRef.current = null;
            isMapLoadedRef.current = false;
            pendingSelectedIdRef.current = null;
            didFireReadyRef.current = false;
        };
    }, [accessToken, styleUrl]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        const source = map.getSource(SOURCE_ID);
        if (!source) return;

        source.setData(storesGeoJSON);

        if (fitToStoresOnUpdate) {
            const bounds = getGeoJSONBounds(storesGeoJSON);
            if (bounds) {
                map.fitBounds(bounds, {
                    padding: fitBoundsPadding,
                    maxZoom: fitBoundsMaxZoom,
                    duration: 600,
                });
            }
        }
    }, [storesGeoJSON, fitToStoresOnUpdate, fitBoundsPadding, fitBoundsMaxZoom]);

    // ✅ NEW: keep user locator updated
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoadedRef.current) return;

        const src = map.getSource(USER_SOURCE_ID);
        if (!src) return;

        src.setData(toPointGeoJSON(userCoords) || { type: "FeatureCollection", features: [] });
    }, [userCoords]);

    // ✅ NEW: react to sidebar selection
    useEffect(() => {
        if (!selectedStoreId) return;

        const id = String(selectedStoreId);
        const map = mapRef.current;

        // if map isn't loaded yet, store it and apply on load
        if (!map || !isMapLoadedRef.current) {
            pendingSelectedIdRef.current = id;
            return;
        }

        const store = storeById.get(id);
        if (!store) return;

        map.easeTo({
            center: store.coordinates,
            zoom: selectedStoreZoom,
            duration: 700,
        });

        openStorePopupRef.current?.(map, store);
    }, [selectedStoreId, storeById, selectedStoreZoom]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: "100%",
                height: "100%",
                borderRadius: 2,
                overflow: "hidden",
                ...sx,
            }}
        />
    );
}